import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Boxes,
  Plus,
  Loader2,
  Pencil,
  Archive,
  Trash2,
  Package,
  Tag,
  Sparkles,
  TrendingDown,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useConfirm } from "../components/ConfirmDialog";

const emptyForm = {
  name: "",
  description: "",
  components: [],
  pack_price_chf: "",
  vat_rate: 2.6,
};

function fmtCHF(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} CHF`;
}

export default function Packs() {
  const confirm = useConfirm();
  const [packs, setPacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: pr }] = await Promise.all([
        api.get(`/packs?include_archived=${includeArchived}`),
        api.get("/products"),
      ]);
      setPacks(p || []);
      setProducts(pr || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [includeArchived]);

  const productMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  // Référence : somme des prix B2B (price_distributor) × quantité
  const referenceTotalCHF = useMemo(() => {
    return form.components.reduce((acc, c) => {
      const p = productMap[c.product_id];
      const ref = p?.price_distributor || p?.price_online || p?.price_direct || 0;
      return acc + ref * (Number(c.quantity) || 0);
    }, 0);
  }, [form.components, productMap]);

  const savings = useMemo(() => {
    const price = Number(form.pack_price_chf) || 0;
    return Math.round((referenceTotalCHF - price) * 100) / 100;
  }, [referenceTotalCHF, form.pack_price_chf]);

  const savingsPct = useMemo(() => {
    if (referenceTotalCHF <= 0) return 0;
    return Math.round((savings / referenceTotalCHF) * 1000) / 10;
  }, [referenceTotalCHF, savings]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (pack) => {
    setEditing(pack);
    setForm({
      name: pack.name || "",
      description: pack.description || "",
      components: (pack.components || []).map((c) => ({
        product_id: c.product_id,
        quantity: c.quantity,
      })),
      pack_price_chf: String(pack.pack_price_chf ?? ""),
      vat_rate: pack.vat_rate ?? 2.6,
    });
    setOpen(true);
  };

  const addComponent = () => {
    setForm((f) => ({
      ...f,
      components: [...f.components, { product_id: "", quantity: 1 }],
    }));
  };

  const updateComponent = (idx, patch) => {
    setForm((f) => ({
      ...f,
      components: f.components.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const removeComponent = (idx) => {
    setForm((f) => ({
      ...f,
      components: f.components.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    if (form.components.length === 0) { toast.error("Ajoutez au moins un composant"); return; }
    for (const c of form.components) {
      if (!c.product_id) { toast.error("Chaque composant doit référencer un produit"); return; }
      if (!c.quantity || Number(c.quantity) <= 0) { toast.error("Quantité invalide"); return; }
    }
    const price = Number(form.pack_price_chf);
    if (!Number.isFinite(price) || price < 0) { toast.error("Prix du pack invalide"); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        components: form.components.map((c) => ({
          product_id: c.product_id,
          quantity: Number(c.quantity),
        })),
        pack_price_chf: price,
        vat_rate: Number(form.vat_rate) || 2.6,
      };
      if (editing) {
        await api.put(`/packs/${editing.id}`, payload);
        toast.success("Pack mis à jour");
      } else {
        await api.post("/packs", payload);
        toast.success("Pack créé");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const archivePack = async (pack) => {
    const ok = await confirm({
      title: pack.archived ? "Réactiver ce pack ?" : "Archiver ce pack ?",
      description: pack.archived
        ? `« ${pack.name} » redeviendra utilisable dans les commandes B2B.`
        : `« ${pack.name} » sera masqué du sélecteur de pack, mais reste consultable.`,
    });
    if (!ok) return;
    try {
      if (pack.archived) {
        await api.put(`/packs/${pack.id}`, { archived: false });
        toast.success("Pack réactivé");
      } else {
        await api.delete(`/packs/${pack.id}`);
        toast.success("Pack archivé");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deletePack = async (pack) => {
    const ok = await confirm({
      title: "Supprimer définitivement ?",
      description: `« ${pack.name} » sera supprimé de la base. Action irréversible.`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/packs/${pack.id}?hard=true`);
      toast.success("Pack supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // KPIs
  const activeCount = packs.filter((p) => !p.archived).length;
  const totalSavings = useMemo(() => {
    let s = 0;
    for (const pack of packs) {
      if (pack.archived) continue;
      let ref = 0;
      for (const c of (pack.components || [])) {
        const p = productMap[c.product_id];
        const r = p?.price_distributor || p?.price_online || p?.price_direct || 0;
        ref += r * (c.quantity || 0);
      }
      s += Math.max(0, ref - (pack.pack_price_chf || 0));
    }
    return s;
  }, [packs, productMap]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="packs-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Packs prêts-à-vendre</h1>
          <p className="text-sm text-[#575A57]">
            Assortiments B2B pré-construits avec prix groupé attractif. Insérables en 1 clic dans une commande.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#1F4E3D] hover:bg-[#173B2E]"
          data-testid="create-pack-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouveau pack
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Packs actifs</div>
          <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{activeCount}</div>
        </CardContent></Card>
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Économie totale offerte</div>
          <div className="font-mono text-2xl text-[#1F4E3D] mt-1">{fmtCHF(totalSavings)}</div>
          <div className="text-[10px] text-[#A3A6A3] mt-1">vs prix B2B catalogue cumulé</div>
        </CardContent></Card>
        <Card className="border-[#E5E5E0]"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Référentiel produits</div>
          <div className="font-mono text-2xl text-[#1A1C1A] mt-1">{products.length}</div>
          <div className="text-[10px] text-[#A3A6A3] mt-1">disponibles pour composer un pack</div>
        </CardContent></Card>
      </div>

      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-xs text-[#575A57] cursor-pointer">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="toggle-archived"
          />
          Afficher les packs archivés
        </label>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Composants</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Prix pack</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Référentiel</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Économie</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="packs-table-body">
            {packs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#575A57] py-10">
                  Aucun pack défini. Cliquez sur <span className="font-medium">« Nouveau pack »</span> pour créer un assortiment B2B.
                </TableCell>
              </TableRow>
            ) : packs.map((pack) => {
              let ref = 0;
              const compsDisplay = [];
              for (const c of (pack.components || [])) {
                const p = productMap[c.product_id];
                const r = p?.price_distributor || p?.price_online || p?.price_direct || 0;
                ref += r * (c.quantity || 0);
                compsDisplay.push({ name: p?.name || "(produit introuvable)", qty: c.quantity });
              }
              const sav = Math.round((ref - (pack.pack_price_chf || 0)) * 100) / 100;
              const savPct = ref > 0 ? Math.round((sav / ref) * 1000) / 10 : 0;
              return (
                <TableRow key={pack.id} data-testid={`pack-row-${pack.id}`} className={pack.archived ? "opacity-50" : ""}>
                  <TableCell>
                    <div className="font-medium text-[#1A1C1A]">{pack.name}</div>
                    {pack.description && (
                      <div className="text-xs text-[#575A57] mt-0.5 line-clamp-2 max-w-md">{pack.description}</div>
                    )}
                    {pack.archived && <Badge variant="outline" className="mt-1 text-[10px]">Archivé</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5 text-xs">
                      {compsDisplay.slice(0, 4).map((c, i) => (
                        <div key={i} className="text-[#575A57]">
                          <span className="font-mono text-[#1F4E3D]">×{c.qty}</span> {c.name}
                        </div>
                      ))}
                      {compsDisplay.length > 4 && (
                        <div className="text-[#A3A6A3] italic">+{compsDisplay.length - 4} autres…</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-[#1F4E3D]">
                    {fmtCHF(pack.pack_price_chf)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-[#A3A6A3]">
                    {fmtCHF(ref)}
                  </TableCell>
                  <TableCell className="text-right">
                    {sav > 0 ? (
                      <div className="flex items-center justify-end gap-1 text-[#1F4E3D]">
                        <TrendingDown className="w-3 h-3" />
                        <span className="font-mono text-sm font-semibold">{fmtCHF(sav)}</span>
                        <span className="text-[10px] text-[#A3A6A3]">({savPct}%)</span>
                      </div>
                    ) : (
                      <span className="text-xs text-[#A3A6A3]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(pack)}
                        title="Modifier"
                        data-testid={`edit-pack-${pack.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => archivePack(pack)}
                        title={pack.archived ? "Réactiver" : "Archiver"}
                        data-testid={`archive-pack-${pack.id}`}
                      >
                        <Archive className={`w-4 h-4 ${pack.archived ? "text-[#1F4E3D]" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deletePack(pack)}
                        title="Supprimer définitivement"
                        data-testid={`delete-pack-${pack.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialog création / édition */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Boxes className="w-5 h-5 text-[#1F4E3D]" />
              {editing ? "Modifier le pack" : "Nouveau pack B2B"}
            </DialogTitle>
            <DialogDescription>
              Définissez un assortiment de produits et son prix groupé. À l'insertion en commande, chaque produit deviendra une ligne avec un prix unitaire réparti proportionnellement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Nom du pack *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex : Pack Découverte Restaurant — 12 barquettes"
                data-testid="pack-form-name"
              />
            </div>

            <div>
              <Label className="mb-1.5 block">Description / argument commercial</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ex : Assortiment des 4 best-sellers, format dégustation. Idéal pour une carte midi rotative."
                rows={2}
                data-testid="pack-form-description"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Composants *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addComponent}
                  data-testid="pack-add-component"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un produit
                </Button>
              </div>

              {form.components.length === 0 ? (
                <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-4 text-center">
                  Cliquez sur « Ajouter un produit » pour composer le pack.
                </div>
              ) : (
                <div className="space-y-2">
                  {form.components.map((c, idx) => {
                    const p = productMap[c.product_id];
                    const ref = p?.price_distributor || p?.price_online || p?.price_direct || 0;
                    const lineRef = ref * (Number(c.quantity) || 0);
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 border border-[#E5E5E0] rounded bg-[#FAFAF7]">
                        <div className="col-span-7">
                          <Label className="text-[11px] mb-1 block">Produit</Label>
                          <Select
                            value={c.product_id || undefined}
                            onValueChange={(v) => updateComponent(idx, { product_id: v })}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`pack-comp-product-${idx}`}>
                              <SelectValue placeholder="Choisir un produit…" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...products].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })).map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                  {p.price_distributor ? ` · ${Number(p.price_distributor).toFixed(2)} CHF` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[11px] mb-1 block">Quantité</Label>
                          <NumericInput
                            value={c.quantity}
                            onChange={(e) => updateComponent(idx, { quantity: e.target.value })}
                            className="h-8 text-xs"
                            data-testid={`pack-comp-qty-${idx}`}
                          />
                        </div>
                        <div className="col-span-2 text-xs text-right">
                          <Label className="text-[11px] mb-1 block opacity-0">.</Label>
                          <div className="h-8 flex items-center justify-end font-mono text-[#575A57]">
                            {lineRef > 0 ? `${lineRef.toFixed(2)} CHF` : "—"}
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Label className="text-[11px] mb-1 block opacity-0">.</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeComponent(idx)}
                            className="h-8 w-8 text-[#C05C47]"
                            data-testid={`pack-comp-remove-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Prix du pack TTC *</Label>
                <NumericInput
                  value={form.pack_price_chf}
                  onChange={(e) => setForm({ ...form, pack_price_chf: e.target.value })}
                  placeholder="120.00"
                  data-testid="pack-form-price"
                />
                <p className="text-[10px] text-[#A3A6A3] mt-1">Prix unique tout compris payé par le revendeur.</p>
              </div>
              <div>
                <Label className="mb-1.5 block">TVA %</Label>
                <NumericInput
                  value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                  data-testid="pack-form-vat"
                />
              </div>
            </div>

            {/* Récap économie */}
            {form.components.length > 0 && referenceTotalCHF > 0 && (
              <div className="p-4 rounded-lg bg-[#EAF4EE] border border-[#C5DBCF]" data-testid="pack-savings-summary">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#1F4E3D] font-semibold">
                  <Sparkles className="w-3.5 h-3.5" /> Aperçu de la remise pack
                </div>
                <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-[#575A57] uppercase">Prix B2B cumulé</div>
                    <div className="font-mono text-[#1A1C1A]">{fmtCHF(referenceTotalCHF)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#575A57] uppercase">Prix du pack</div>
                    <div className="font-mono text-[#1F4E3D] font-semibold">{fmtCHF(Number(form.pack_price_chf) || 0)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#575A57] uppercase">Économie offerte</div>
                    <div className="font-mono text-[#1F4E3D] font-semibold flex items-center gap-1">
                      <TrendingDown className="w-3.5 h-3.5" />
                      {fmtCHF(Math.max(0, savings))} <span className="text-[10px]">({Math.max(0, savingsPct)}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setForm(emptyForm); setEditing(null); }}>
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="save-pack-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? "Enregistrer" : "Créer le pack"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
